import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaSync, FaInfoCircle } from "react-icons/fa";
import { SipDropdown, SipSearch } from "../../components/form/SipControls";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Api from "../../api";
import EditCoeModal from "../../components/coe/EditCoeModal";
import CoeCalendar, {
  CoeDatePopup,
  STATUS_LABEL,
  UNIT_LABEL,
  dateKey,
  eventsOnDate,
} from "../../components/coe/CoeCalendar";

function CoePage() {
  const today = dayjs();
  const [rows, setRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [year, setYear] = useState(today.year());
  const [month, setMonth] = useState(today.month());
  const [view, setView] = useState("year");
  const [selectedKey, setSelectedKey] = useState(null);
  const [editing, setEditing] = useState(null);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await Api.get("/sp/teams", {
        params: { per_page: 200, page: 1 },
      });
      const list = res.data?.data || res.data || [];
      setTeams(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Gagal memuat teams:", error);
      setTeams([]);
    }
  }, []);

  const fetchCoe = useCallback(async () => {
    setLoading(true);
    try {
      let page = 1;
      let totalPages = 1;
      const all = [];
      do {
        const params = { page, per_page: 200, search: appliedSearch };
        if (statusFilter) params.status = statusFilter;
        if (unitFilter) params.unit = unitFilter;
        const res = await Api.get("/sp/coe/tahunan", { params });
        all.push(...(res.data?.data || []));
        totalPages = res.data?.total_pages || 1;
        page += 1;
      } while (page <= totalPages && page <= 20);
      setRows(all);
    } catch (error) {
      console.error("Gagal memuat COE:", error);
      toast.error(
        error.response?.data?.message ||
          "Gagal memuat data COE. Pastikan server berjalan."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, statusFilter, unitFilter]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    fetchCoe();
  }, [fetchCoe]);

  const yearEvents = useMemo(() => {
    const start = `${year}-01-01`;
    const end = `${year}-12-31`;
    return rows.filter((item) => {
      const from = dateKey(item.tgl);
      const to = dateKey(item.end) || from;
      return from <= end && to >= start;
    });
  }, [rows, year]);

  const popupEvents = useMemo(
    () => eventsOnDate(yearEvents, selectedKey),
    [yearEvents, selectedKey]
  );

  const handleSearch = (event) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
  };

  const handleUpdate = async (payload) => {
    if (!editing?.id) return;
    setSaving(true);
    try {
      const res = await Api.put(`/sp/coe/tahunan/${editing.id}`, payload);
      if (res.data?.status === "success") {
        toast.success("COE berhasil diupdate");
        setEditing(null);
        fetchCoe();
      } else {
        toast.error(res.data?.message || "Gagal mengupdate COE");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal mengupdate COE");
    } finally {
      setSaving(false);
    }
  };

  const shift = (direction) => {
    if (view === "year") {
      setYear((value) => value + direction);
      return;
    }
    const next = dayjs(new Date(year, month, 1)).add(direction, "month");
    setYear(next.year());
    setMonth(next.month());
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
        <FaInfoCircle className="mt-0.5 shrink-0" />
        <p>
          Kalender ini hanya untuk <strong>melihat dan mengubah</strong> COE
          yang sudah ada. Kegiatan baru tetap dibuat di{" "}
          <strong>SIP StelkPro</strong>.
        </p>
      </div>

      <form
        onSubmit={handleSearch}
        className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-3 shadow-sm sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_11rem_10rem_auto_auto] xl:items-center"
      >
        <div className="min-w-0 sm:col-span-2 xl:col-span-1">
          <SipSearch
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Cari kegiatan, lokasi, team..."
          />
        </div>
        <SipDropdown
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          placeholder="Semua Status"
          options={[
            { label: "Semua Status", value: "" },
            ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
          ]}
        />
        <SipDropdown
          value={unitFilter}
          onChange={(event) => setUnitFilter(event.target.value)}
          placeholder="Semua Unit"
          options={[
            { label: "Semua Unit", value: "" },
            ...Object.entries(UNIT_LABEL).map(([value, label]) => ({ value, label })),
          ]}
        />
        <button
          type="submit"
          className="w-full rounded-xl bg-primary px-4 py-2.5 text-white hover:bg-primary/90 xl:w-auto"
        >
          Cari
        </button>
        <button
          type="button"
          onClick={() => fetchCoe()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-gray-700 hover:bg-gray-50 xl:w-auto"
        >
          <FaSync className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </form>

      <CoeCalendar
        year={year}
        month={month}
        view={view}
        events={yearEvents}
        loading={loading}
        selectedKey={selectedKey}
        onToday={() => {
          setYear(today.year());
          setMonth(today.month());
        }}
        onPrev={() => shift(-1)}
        onNext={() => shift(1)}
        onView={setView}
        onPickDate={setSelectedKey}
      />

      <CoeDatePopup
        dateKey={popupEvents.length ? selectedKey : null}
        events={popupEvents}
        onClose={() => setSelectedKey(null)}
        onEdit={(item) => {
          setSelectedKey(null);
          setEditing(item);
        }}
      />

      <EditCoeModal
        open={!!editing}
        item={editing}
        teams={teams}
        loading={saving}
        onClose={() => setEditing(null)}
        onSubmit={handleUpdate}
      />
    </div>
  );
}

export default CoePage;
